import { Play } from "@phosphor-icons/react";
import { useState, KeyboardEvent } from "react";

import { type OpellaVideo } from "../mockData";

interface VideoCardProps {
  video: OpellaVideo;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onPlay: (id: string) => void;
}

function brandInitial(brand: string): string {
  return brand.charAt(0).toUpperCase();
}

export function VideoCard({
  video,
  selected,
  onToggleSelect,
  onPlay,
}: VideoCardProps) {
  const [thumbFailed, setThumbFailed] = useState(false);

  const handlePosterClick = () => {
    onPlay(video.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onPlay(video.id);
    }
  };

  const handleCheckboxChange = () => {
    onToggleSelect(video.id);
  };

  return (
    <div
      className="opella-card relative overflow-hidden"
      data-selected={selected ? "true" : "false"}
      style={{
        borderColor: selected
          ? "var(--opella-accent)"
          : "var(--opella-sidebar-border)",
        boxShadow: selected
          ? "0 0 0 2px var(--opella-accent-ring), var(--opella-shadow-elevated)"
          : undefined,
      }}
    >
      <button
        type="button"
        onClick={handlePosterClick}
        onKeyDown={handleKeyDown}
        aria-label={`Play ${video.title}`}
        className="group relative block aspect-video w-full overflow-hidden"
        style={{ background: "var(--opella-accent-soft)" }}
      >
        {thumbFailed ? (
          <div
            className="flex size-full flex-col items-center justify-center gap-2 p-6 text-center"
            style={{
              background:
                "linear-gradient(135deg, #042b0b 0%, #1d4023 55%, #ff78d2 100%)",
              color: "#ffffff",
            }}
            aria-hidden
          >
            <div className="text-4xl font-bold tracking-tight">
              {brandInitial(video.brand)}
            </div>
            <div className="text-xs font-medium uppercase tracking-wider opacity-85">
              {video.brand}
            </div>
          </div>
        ) : (
          <img
            src={video.thumbnailSrc}
            alt=""
            loading="lazy"
            className="size-full object-cover"
            onError={() => setThumbFailed(true)}
          />
        )}
        <div
          className="bg-black/0 group-hover:bg-black/20 group-focus-visible:bg-black/20 pointer-events-none absolute inset-0 flex items-center justify-center transition-colors"
          aria-hidden
        >
          <span
            className="flex size-14 items-center justify-center rounded-full opacity-0 transition-all group-hover:opacity-100 group-focus-visible:opacity-100"
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              color: "var(--opella-accent)",
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
            }}
          >
            <Play size={22} weight="fill" aria-hidden />
          </span>
        </div>
        <span
          className="absolute bottom-3 right-3 rounded-md px-2 py-1 text-[11px] font-medium tabular-nums"
          style={{
            background: "rgba(23, 18, 58, 0.85)",
            color: "#ffffff",
          }}
        >
          {video.duration}
        </span>
      </button>

      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0 flex-1">
          <p
            className="text-[13px] font-semibold uppercase tracking-wider"
            style={{ color: "var(--opella-primary)" }}
          >
            {video.brand}
          </p>
          <h3
            className="mt-1 line-clamp-2 text-[15px] font-semibold leading-snug"
            style={{ color: "var(--opella-fg)" }}
          >
            {video.title}
          </h3>
          <div
            className="mt-2 flex flex-wrap gap-1.5 text-xs"
            style={{ color: "var(--opella-muted)" }}
          >
            {video.markets.map((market) => (
              <span
                key={market}
                className="opella-pill"
                style={{
                  background: "var(--opella-sidebar-bg)",
                  color: "var(--opella-fg-secondary)",
                }}
              >
                {market}
              </span>
            ))}
          </div>
        </div>
      </div>

      <label
        className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-md"
        style={{
          background: selected
            ? "var(--opella-accent)"
            : "rgba(255,255,255,0.9)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      >
        <input
          type="checkbox"
          className="sr-only"
          checked={selected}
          onChange={handleCheckboxChange}
          aria-label={`Select ${video.title}`}
        />
        <span
          className="flex size-6 items-center justify-center rounded-md"
          aria-hidden
          style={{
            color: selected ? "#ffffff" : "var(--opella-fg-secondary)",
          }}
        >
          {selected ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect
                x="4"
                y="4"
                width="16"
                height="16"
                rx="3"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          )}
        </span>
      </label>
    </div>
  );
}
